<script>
  import Modal from "@components/shared/modal.svelte";
  import Autocomplete from "@components/shared/autocomplete.svelte";
  import Button from "@components/button.svelte";
  import { X, Undo2, CircleCheck, Users } from "lucide-svelte";
  import { get, patch } from "@lib/requests";
  import { showAlert, currentUser } from "@lib/utils";
  import { onMount, tick } from "svelte";

  let {
    text = "Exclusions",
    title = "Exclusions des relances automatiques"
  } = $props();

  // Gate write operations on admin / invoice_manager — non-managers can still open the modal
  // and see who's excluded, but the Appliquer button is locked.
  let canManage = $derived(Boolean(currentUser()?.can_manage_invoices));

  let open = $state(false);
  let loading = $state(false);
  let saving = $state(false);

  // Server snapshot — the "committed" state, set by loadData and re-set after a successful apply.
  let initialExcluded = $state([]);

  // Draft state — what the user sees and edits in the modal. Compared against the snapshot to
  // compute pending changes. Only pushed to the server when the user clicks "Appliquer".
  let excluded = $state([]);

  let autocompleteRef = $state(null);
  let autocompleteValue = $state(null);
  let autocompleteItems = $state([]);

  // Derived diffs between draft and snapshot.
  let addedIds = $derived(excluded.filter((c) => !initialExcluded.some((i) => i.id === c.id)).map((c) => c.id));
  let removedIds = $derived(initialExcluded.filter((i) => !excluded.some((c) => c.id === i.id)).map((i) => i.id));
  let hasChanges = $derived(addedIds.length > 0 || removedIds.length > 0);

  async function loadData() {
    try {
      loading = true;
      const response = await get("/api/v1/invoice_reminders/excluded_clients");
      const data = await response.json;
      initialExcluded = data.clients || [];
      excluded = [...initialExcluded];
    } catch (e) {
      showAlert(e.message, { type: "error" });
    } finally {
      loading = false;
    }
  }

  function openModal() {
    open = true;
    loadData();
    // Focus the autocomplete after the modal has rendered its content.
    tick().then(() => autocompleteRef?.focus?.());
  }

  // Load on mount so the button's superscript count is accurate before the user opens the modal.
  onMount(loadData);

  function closeModal() {
    if (hasChanges && !window.confirm("Des modifications n'ont pas été enregistrées. Fermer quand même ?")) {
      open = true; // cancel the close
      return;
    }
    open = false;
    initialExcluded = [];
    excluded = [];
    autocompleteValue = null;
  }

  // Staged edits — update the draft only. No server calls until `applyChanges`.
  function removeFromExclusions(clientId) {
    excluded = excluded.filter((c) => c.id !== clientId);
  }

  function addToExclusions(client) {
    if (!client) return;
    if (excluded.some((c) => c.id === client.id)) {
      showAlert("Ce client est déjà exclus", { type: "info" });
      autocompleteRef?.clearValue?.();
      return;
    }
    const entry = { id: client.id, name: client.name, reference: client.reference };
    excluded = [...excluded, entry].sort((a, b) => a.name.localeCompare(b.name));
    autocompleteRef?.clearValue?.();
  }

  function revertChanges() {
    excluded = [...initialExcluded];
  }

  async function applyChanges() {
    if (!hasChanges) return;
    saving = true;
    try {
      const requests = [];
      for (const id of addedIds) {
        requests.push(patch("/api/v1/invoice_reminders/toggle_client", { body: { client_id: id, enabled: false } }));
      }
      for (const id of removedIds) {
        requests.push(patch("/api/v1/invoice_reminders/toggle_client", { body: { client_id: id, enabled: true } }));
      }
      await Promise.all(requests);
      initialExcluded = [...excluded];
      showAlert("Modifications enregistrées", { type: "success" });
    } catch (e) {
      showAlert("Erreur lors de l'enregistrement: " + e.message, { type: "error" });
    } finally {
      saving = false;
    }
  }
</script>

<Button onclick={openModal}>
  <Users size={14} class="text-gray-500" />
  <span>{text}</span>
  {#if excluded.length > 0}
    <sup class="text-xxs opacity-50 -ml-1 -mr-1">{excluded.length}</sup>
  {/if}
</Button>

<Modal bind:open {title} width="48rem" onClose={closeModal}>
  {#snippet footerButtons()}
    <Button
      text="Rétablir"
      Icon={Undo2}
      disabled={!hasChanges || saving}
      onclick={revertChanges}
    />
    <Button
      text={saving ? "Enregistrement..." : "Appliquer"}
      Icon={CircleCheck}
      loading={saving}
      disabled={!hasChanges || saving || !canManage}
      hint={canManage ? null : "Réservé aux gestionnaires de factures"}
      class="outline-submit"
      onclick={applyChanges}
    />
  {/snippet}

  {#if loading}
    <div class="app-message">
      <i class="fal fa-spinner fa-spin"></i>
      <p>Chargement...</p>
    </div>
  {:else}

    <!-- Add-to-exclusion autocomplete (full width) -->
    <div class="form-item mb-6 w-full autocomplete-full-width">
      <label class="label" for="exclude-client-input">Exclure un client</label>
      <Autocomplete
        bind:this={autocompleteRef}
        bind:value={autocompleteValue}
        bind:items={autocompleteItems}
        url="/api/v1/clients?allow_disabled=true"
        queryKey="q"
        labelKey="name"
        valueKey="id"
        placeholder="Rechercher un client à exclure..."
        showList={true}
        onValueChange={addToExclusions}
        id="exclude-client-input"
      />
    </div>

    <!-- Excluded clients -->
    <div class="w-full">
      <p class="label mb-3">
        Clients exclus des relances automatiques
        {#if excluded.length > 0}
          <span class="text-gray-400 font-normal">({excluded.length})</span>
        {/if}
      </p>

      {#if excluded.length === 0}
        <p class="text-sm text-gray-500 italic">Aucun client exclu pour l'instant.</p>
      {:else}
        <div class="flex flex-wrap gap-2">
          {#each excluded as client (client.id)}
            <div class="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-sm text-xs text-slate-500">
              <span>{client.reference ? `${client.name} (${client.reference})` : client.name}</span>
              <Button
                naked
                Icon={X}
                iconSize={12}
                hint="Retirer de la liste d'exclusion"
                class="text-slate-400 hover:text-slate-700"
                onclick={() => removeFromExclusions(client.id)}
              />
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</Modal>

<style>
  /* The Popover inside Autocomplete wraps its trigger in a `div.trigger` which defaults to
     `display: inline-block` — sizing to content. Forcing block + w-full on that wrapper makes
     the autocomplete input fill the form item. */
  :global(.autocomplete-full-width > .trigger) {
    display: block;
    width: 100%;
  }

  /* Slightly smaller input text for the exclusion autocomplete (overrides the component's
     default text-sm). Scoped so it only applies to this instance. */
  :global(.autocomplete-full-width input) {
    font-size: 0.8rem;
  }
</style>
